// src/contexts/AppContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

// Direct REST fetch — bypasses supabase.from() which internally calls getSession()
// and contends on the Supabase auth mutex when AuthContext is also calling getSession().
const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const FOUNDER_EMAIL = "charles@fyrris.com";

// Admin impersonation — shared key and event name used across the app.
// Dispatch the event whenever localStorage is written/cleared so AppContext
// can update its reactive state.
export const IMPERSONATE_KEY   = "fyrris_impersonate_manufacturer_id";
export const IMPERSONATE_EVENT = "fyrris:impersonate-changed";

export const RETAILER_IMPERSONATE_KEY   = "fyrris_impersonate_retailer_id";
export const RETAILER_IMPERSONATE_EVENT = "fyrris_retailer_impersonate_change";

export type ProfileRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  store_name: string | null;
  phone: string | null;
  role: "admin" | "retailer" | null;
  stripe_customer_id: string | null;
  is_active: boolean | null;
  onboarding_complete: boolean | null;
  kit_received: boolean | null;
  created_at: string | null;
};

interface AppContextType {
  // Layout
  sidebarOpen: boolean;
  toggleSidebar: () => void;

  // Auth + profile
  loading: boolean;
  session: Session | null;
  user: User | null;
  profile: ProfileRow | null;
  isAdmin: boolean;
  isActive: boolean;
  needsOnboarding: boolean;

  // Manufacturer portal
  isManufacturerUser: boolean;
  manufacturerId: string | null;

  // Retailer portal
  retailerId: string | null;

  // Demo mode
  isDemo: boolean;

  // Actions
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const defaultValue: AppContextType = {
  sidebarOpen: false,
  toggleSidebar: () => {},

  loading: true,
  session: null,
  user: null,
  profile: null,
  isAdmin: false,
  isActive: false,
  needsOnboarding: false,
  isManufacturerUser: false,
  manufacturerId: null,
  retailerId: null,
  isDemo: false,

  refreshProfile: async () => {},
  signOut: async () => {},
};

const AppContext = createContext<AppContextType>(defaultValue);
export const useAppContext = () => useContext(AppContext);

// Fetch profile via direct REST — no Supabase client auth mutex involved.
async function fetchProfile(userId: string, jwt: string): Promise<ProfileRow | null> {
  try {
    const res = await fetch(
      `${SB_URL}/rest/v1/profiles?id=eq.${userId}&select=id,email,full_name,first_name,last_name,store_name,phone,role,stripe_customer_id,is_active,onboarding_complete,kit_received,created_at&limit=1`,
      { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
    );
    if (!res.ok) return null;
    const [row]: ProfileRow[] = await res.json();
    return row ?? null;
  } catch {
    return null;
  }
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Layout
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarOpen((p) => !p), []);

  // Auth / data
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [isManufacturerUser, setIsManufacturerUser] = useState(false);
  const [manufacturerId, setManufacturerId] = useState<string | null>(null);
  const [retailerId, setRetailerId] = useState<string | null>(null);
  // Tracks the last-known signed-in user id across onAuthStateChange events,
  // so the handler can tell "same identity, session object rotated" apart
  // from "an actual sign-in/sign-out/account-switch" — see that handler's
  // own comment for why event-name allowlisting (the previous approach,
  // TOKEN_REFRESHED only) isn't enough. Starts null; the very first event
  // (INITIAL_SESSION on subscribe) always compares unequal to null for a
  // real signed-in user, so the genuine first bootstrap is never mistaken
  // for "same user" — no separate getSession() seed call needed (and the
  // comment below already explains why one isn't added here).
  const previousUserIdRef = useRef<string | null>(null);

  // Manufacturer impersonation — reactive localStorage tracker.
  const [impersonatedMfrId, setImpersonatedMfrId] = useState<string | null>(
    () => localStorage.getItem(IMPERSONATE_KEY),
  );
  useEffect(() => {
    const handler = () => setImpersonatedMfrId(localStorage.getItem(IMPERSONATE_KEY));
    window.addEventListener(IMPERSONATE_EVENT, handler);
    return () => window.removeEventListener(IMPERSONATE_EVENT, handler);
  }, []);

  // Retailer impersonation — reactive localStorage tracker.
  const [impersonatedRetailerId, setImpersonatedRetailerId] = useState<string | null>(
    () => localStorage.getItem(RETAILER_IMPERSONATE_KEY),
  );
  useEffect(() => {
    const handler = () => setImpersonatedRetailerId(localStorage.getItem(RETAILER_IMPERSONATE_KEY));
    window.addEventListener(RETAILER_IMPERSONATE_EVENT, handler);
    return () => window.removeEventListener(RETAILER_IMPERSONATE_EVENT, handler);
  }, []);

  // ---- Session bootstrap & listener ----
  useEffect(() => {
    let mounted = true;

    // Do NOT call supabase.auth.getSession() here — AuthContext already holds
    // the mutex. onAuthStateChange fires INITIAL_SESSION synchronously from
    // localStorage on subscribe, which is sufficient to bootstrap state.

    // Safety net: if INITIAL_SESSION never fires, clear loading after 5 s.
    const safetyTimeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 5000);

    const { data: sub } = supabase.auth.onAuthStateChange(async (evt, sess) => {
      if (!mounted) return;
      clearTimeout(safetyTimeout);

      // Identity comparison, not event-name allowlisting. The previous
      // version only special-cased TOKEN_REFRESHED — but
      // @supabase/auth-js's own _recoverAndRefresh() (run on every tab
      // 'visibilitychange' -> visible) fires plain SIGNED_IN, with the SAME
      // session/user, whenever the token ISN'T within EXPIRY_MARGIN_MS
      // (90s) of expiring — i.e. on ~every ordinary tab refocus, not just
      // the rare refocus-in-the-last-90-seconds-of-an-hour case
      // TOKEN_REFRESHED covers. That gap is exactly how this bug (guarded
      // children — an open modal, the product wizard mid-fill — unmounting
      // on tab refocus) existed: allowlisting one event name doesn't cover
      // the next one the SDK fires for the same "nothing actually changed"
      // situation. Comparing WHO is signed in, instead of WHICH event
      // fired, covers TOKEN_REFRESHED, this SIGNED_IN case, and any future
      // same-user event the SDK ever adds, by construction.
      const newUserId = sess?.user?.id ?? null;
      const sameUser = newUserId !== null && newUserId === previousUserIdRef.current;
      previousUserIdRef.current = newUserId;

      if (sameUser) {
        // Same identity as before — update the (possibly rotated) session
        // object silently and never re-enter loading. setLoading(true)
        // here flips RequireAuth/RequireAdmin/RequireManufacturer's
        // appLoading true, which unmounts every guarded child for no
        // reason, wiping any local state (an open modal, in-progress form
        // input, upload progress) with it. Same tradeoff as the code this
        // replaces: a same-user event does not re-check role/profile
        // staleness (e.g. a revoked admin role won't be noticed until an
        // actual identity-changing event) — carried forward deliberately
        // unchanged; fixing that is separate, not this change's job.
        setSession(sess ?? null);
        return;
      }

      setLoading(true);
      try {
        setSession(sess ?? null);
        const u = sess?.user ?? null;
        setUser(u);
        if (sess && u) {
          // Use direct fetch — avoids supabase.from() calling getSession()
          // internally which deadlocks when AuthContext has the mutex locked.
          const p = await fetchProfile(u.id, sess.access_token);
          if (mounted) setProfile(p);

          // Derive admin status directly from the just-fetched profile so we
          // can skip the manufacturer_users query for admins. Admins access
          // manufacturer data via localStorage impersonation, not this table.
          const freshIsAdmin = p?.role === "admin" || u.email === FOUNDER_EMAIL;

          if (freshIsAdmin) {
            if (mounted) {
              setIsManufacturerUser(false);
              setManufacturerId(null);
              setRetailerId(null); // admins use impersonation, not real retailer rows
            }
          } else {
            // Guard: supabase.from() calls getSession() internally which can
            // deadlock against the auth mutex. Race against a 3-second timeout
            // so loading always resolves even if the query hangs.
            const queryMfrUser = async (): Promise<{ manufacturer_id: string } | null> => {
              const { data } = await supabase
                .from("manufacturer_users")
                .select("manufacturer_id")
                .eq("user_id", u.id)
                .maybeSingle();
              return data as { manufacturer_id: string } | null;
            };
            const timeoutFallback = new Promise<null>((resolve) =>
              setTimeout(() => resolve(null), 3000)
            );
            try {
              const mfrRow = await Promise.race([queryMfrUser(), timeoutFallback]);
              if (mounted) {
                setIsManufacturerUser(!!mfrRow);
                setManufacturerId(mfrRow?.manufacturer_id ?? null);
              }
            } catch {
              if (mounted) {
                setIsManufacturerUser(false);
                setManufacturerId(null);
              }
            }

            // Retailer lookup — find this user's retailer row by owner_user_id.
            // Wrapped in try/catch so a missing row or policy error never blocks loading.
            try {
              const retRes = await fetch(
                `${SB_URL}/rest/v1/retailers?owner_user_id=eq.${u.id}&select=id&limit=1`,
                { headers: { apikey: ANON_KEY, Authorization: `Bearer ${sess.access_token}` } }
              );
              if (retRes.ok) {
                const [retRow]: { id: string }[] = await retRes.json();
                if (mounted) setRetailerId(retRow?.id ?? null);
              }
            } catch {
              if (mounted) setRetailerId(null);
            }
          }
        } else {
          setProfile(null);
          setIsManufacturerUser(false);
          setManufacturerId(null);
          setRetailerId(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      sub.subscription.unsubscribe();
    };
  }, []);

  // ---- Public refresh (called after profile edits) ----
  const refreshProfile = useCallback(async () => {
    if (!session || !user) return;
    const p = await fetchProfile(user.id, session.access_token);
    setProfile(p);
  }, [session, user]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setProfile(null);
  }, []);

  // ---- Derived flags ----
  const email = session?.user?.email ?? null;

  const isAdmin = useMemo(() => {
    if (profile?.role === "admin") return true;
    if (!profile && email === FOUNDER_EMAIL) return true;
    return false;
  }, [profile?.role, email]);

  const isActive = useMemo(() => {
    if (profile?.is_active) return true;
    if (!profile && email === FOUNDER_EMAIL) return true;
    return false;
  }, [profile?.is_active, email]);

  // True when the retailer has not yet completed the onboarding wizard
  const needsOnboarding = useMemo(() => {
    if (!profile) return false;
    if (isAdmin) return false;
    return profile.onboarding_complete !== true;
  }, [profile, isAdmin]);

  // Effective manufacturer ID — impersonated ID takes precedence for admins.
  const effectiveManufacturerId = useMemo(
    () => (isAdmin && impersonatedMfrId ? impersonatedMfrId : manufacturerId),
    [isAdmin, impersonatedMfrId, manufacturerId],
  );

  // Effective retailer ID — impersonated ID takes precedence for admins.
  const effectiveRetailerId = useMemo(
    () => (isAdmin && impersonatedRetailerId ? impersonatedRetailerId : retailerId),
    [isAdmin, impersonatedRetailerId, retailerId],
  );

  const DEMO_MFR_ID         = "00000000-0000-0000-0000-000000000001";
  const DEMO_RETAILER_ID    = "00000000-0000-0000-0000-000000000002";
  // Superior Fireplaces: a second demo manufacturer (manufacturers.is_demo = true) with test data only.
  const DEMO_MFR_SUPERIOR_ID = "40f2330a-570b-49a9-8a26-2a1697c4b8fd";
  const DEMO_RETAILER_EMAIL = "demo-retailer@fyrris.com";
  const isDemo = useMemo(
    () =>
      effectiveManufacturerId === DEMO_MFR_ID ||
      effectiveManufacturerId === DEMO_MFR_SUPERIOR_ID ||
      effectiveRetailerId     === DEMO_RETAILER_ID ||
      email                   === DEMO_RETAILER_EMAIL,
    [effectiveManufacturerId, effectiveRetailerId, email],
  );

  const value: AppContextType = useMemo(
    () => ({
      sidebarOpen,
      toggleSidebar,
      loading,
      session,
      user,
      profile,
      isAdmin,
      isActive,
      needsOnboarding,
      isManufacturerUser,
      manufacturerId: effectiveManufacturerId,
      retailerId: effectiveRetailerId,
      isDemo,
      refreshProfile,
      signOut,
    }),
    [sidebarOpen, loading, session, user, profile, isAdmin, isActive, needsOnboarding,
     isManufacturerUser, effectiveManufacturerId, effectiveRetailerId, isDemo, refreshProfile, signOut]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
